import { useTranslation } from "@sagot/i18n";
import { Button, ChoiceChips, Checkbox, Icon, Input } from "@sagot/ui";
import { useState } from "react";
import { Controller, type Control } from "react-hook-form";
import type { Audience } from "../shared/use-engage-data";
import type { ComposerValues } from "./composer-form";
import { CHANNEL_ICONS, CHANNELS } from "./model";

const SEGMENT_MAX = 40;

interface SectionProps {
  control: Control<ComposerValues>;
}

/** Audience chips (tenant labels by code) + optional free-form segments. */
export function AudienceSection({ control, audiences, segmentOptions, error }: SectionProps & { audiences: readonly Audience[]; segmentOptions: readonly string[]; error?: string }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [added, setAdded] = useState<string[]>([]);
  const options = [...new Set([...segmentOptions, ...added])];
  return (
    <div>
      <div className="label">{t("engage.announcements.composer.audience")}</div>
      <Controller
        control={control}
        name="audiences"
        render={({ field }) => (
          <ChoiceChips
            multiple
            aria-label={t("engage.announcements.composer.audience")}
            options={audiences.map((a) => ({ value: String(a.code), label: a.label }))}
            value={field.value.map(String)}
            onValueChange={(next) => {
              field.onChange(next.map(Number));
            }}
          />
        )}
      />
      {error ? (
        <p role="alert" className="mt-1 text-xs font-semibold text-danger">
          {error}
        </p>
      ) : null}
      <div className="mt-3">
        <div className="mb-1.5 text-[11px] font-semibold text-fg-subtle">{t("engage.announcements.composer.segments")}</div>
        <Controller
          control={control}
          name="segments"
          render={({ field }) => (
            <div className="space-y-2">
              {options.length ? (
                <ChoiceChips multiple variant="badge" aria-label={t("engage.announcements.composer.segments")} options={options.map((s) => ({ value: s, label: s }))} value={field.value} onValueChange={field.onChange} />
              ) : null}
              <div className="flex gap-2">
                <Input
                  className="h-8 max-w-48"
                  maxLength={SEGMENT_MAX}
                  value={draft}
                  placeholder={t("engage.announcements.composer.segmentPlaceholder")}
                  aria-label={t("engage.announcements.composer.segmentPlaceholder")}
                  onChange={(e) => {
                    setDraft(e.target.value);
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!draft.trim()}
                  onClick={() => {
                    const value = draft.trim();
                    setAdded((list) => [...list, value]);
                    if (!field.value.includes(value)) field.onChange([...field.value, value]);
                    setDraft("");
                  }}
                >
                  <Icon name="plus" />
                  {t("engage.announcements.composer.addSegment")}
                </Button>
              </div>
              <p className="text-[11px] text-fg-subtle">{t("engage.announcements.composer.segmentsHint")}</p>
            </div>
          )}
        />
      </div>
    </div>
  );
}

/** Channel checkbox cards (push / web app banner / widget pin / email). */
export function ChannelSection({ control }: SectionProps) {
  const { t } = useTranslation();
  return (
    <fieldset>
      <legend className="label">{t("engage.announcements.composer.channels")}</legend>
      <Controller
        control={control}
        name="channels"
        render={({ field }) => (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {CHANNELS.map((channel) => (
              <div key={channel} className="rounded-md border border-line p-3 hover:bg-surface-2">
                <Checkbox
                  checked={field.value.includes(channel)}
                  onCheckedChange={(checked) => {
                    field.onChange(checked === true ? CHANNELS.filter((c) => c === channel || field.value.includes(c)) : field.value.filter((c) => c !== channel));
                  }}
                  label={
                    <span className="flex items-center gap-1.5 font-semibold">
                      <Icon name={CHANNEL_ICONS[channel]} className="size-4 text-primary-ink" />
                      {t(`engage.announcements.channels.${channel}`)}
                    </span>
                  }
                  description={t(`engage.announcements.composer.channelHints.${channel}`)}
                />
              </div>
            ))}
          </div>
        )}
      />
    </fieldset>
  );
}
